import React, { useCallback, useEffect, useState } from "react";
import { CurrentPathProvider } from "../../contexts";
import { ThemeProvider } from "../../components/theme/ThemeProvider";
import { AppToastProvider } from "../../components/feedback/AppToastProvider";
import FlashToaster from "../../components/feedback/FlashToaster";
import { AppHeader } from "../../components/layout/AppHeader";
import {
  DashboardLayout,
  isDashboardPage,
} from "../../components/layout/DashboardLayout";
import {
  AccountPage,
  BillingPage,
  BlogIndexPage,
  BlogPostPage,
  ComparisonPage,
  DashboardPage,
  FaqPage,
  FeaturesPage,
  LandingPage,
  LandingSectionPage,
  LegalFooter,
  ForgotPasswordPage,
  LoginPage,
  JobsListPage,
  JobNewPage,
  JobShowPage,
  CredentialsListPage,
  CredentialNewPage,
  CredentialShowPage,
  CredentialShareViewPage,
  CredentialShareMissingPage,
  OrgMembersPage,
  OrgSetupPage,
  ProductsHomePage,
  PrivacyPage,
  QuotesPage,
  SettingsPage,
  ResetPasswordPage,
  SignupPage,
  TeammateHomePage,
  TermsPage,
  VerifyEmailPage,
  WebsiteSetupPage,
  TradeLandingPage,
  ContractorShowcasePage,
  ProductLandingPage,
} from "./components/index";
import type { LandingSectionSlug } from "./components/marketing/landingSectionContent";
import type { ProductLandingSlug } from "./components/marketing/productLandingContent";
import type { MainAppPageProps, MainPage, UserDisplay } from "./types";

const LANDING_SECTION_PAGE_TO_SLUG: Partial<
  Record<MainPage, LandingSectionSlug>
> = {
  marketing_features: "features",
  marketing_pricing: "pricing",
  marketing_why_toolkit: "why-toolkit",
  marketing_trades: "trades",
  marketing_testimonials: "testimonials",
  marketing_different: "different",
};

const PRODUCT_PAGE_TO_SLUG: Partial<Record<MainPage, ProductLandingSlug>> = {
  marketing_website: "website",
  marketing_job_photos: "job-photos",
  marketing_credentials: "credentials",
};

const mainPageContent = (
  props: MainAppPageProps,
  userDisplay: UserDisplay | null | undefined,
  onUserDisplayChange: (updates: Partial<UserDisplay>) => void,
  unreadQuoteCount: number,
  onQuoteMarkedRead: () => void,
) => (
  <CurrentPathProvider currentPath={props.currentPath}>
    <AppToastProvider>
      <FlashToaster flash={props.flash} />
      {props.page === "home" ? (
        <LandingPage
          loggedIn={props.loggedIn}
          hasBusiness={props.hasBusiness}
          hasOrganization={props.hasOrganization}
          canAccessBackend={props.canAccessBackend}
          routes={props.routes}
          csrfToken={props.csrfToken}
          page={props.page}
          userDisplay={userDisplay}
          trades={props.trades}
          appStore={props.appStore}
        />
      ) : props.page === "trade" && props.trade ? (
        <TradeLandingPage
          trade={props.trade}
          loggedIn={props.loggedIn}
          hasBusiness={props.hasBusiness}
          routes={props.routes}
          csrfToken={props.csrfToken}
          page={props.page}
          userDisplay={userDisplay}
        />
      ) : props.page === "comparison" && props.comparison ? (
        <ComparisonPage
          comparison={props.comparison}
          loggedIn={props.loggedIn}
          hasBusiness={props.hasBusiness}
          routes={props.routes}
          csrfToken={props.csrfToken}
          page={props.page}
          userDisplay={userDisplay}
        />
      ) : props.page === "blog_index" ? (
        <BlogIndexPage
          posts={props.blogPosts ?? []}
          loggedIn={props.loggedIn}
          hasBusiness={props.hasBusiness}
          routes={props.routes}
          csrfToken={props.csrfToken}
          page={props.page}
          userDisplay={userDisplay}
        />
      ) : props.page === "blog_post" && props.blogPost ? (
        <BlogPostPage
          post={props.blogPost}
          loggedIn={props.loggedIn}
          hasBusiness={props.hasBusiness}
          routes={props.routes}
          csrfToken={props.csrfToken}
          page={props.page}
          userDisplay={userDisplay}
        />
      ) : props.page === "marketing_contractor_showcase" ? (
        <ContractorShowcasePage
          sites={props.contractorShowcaseSites ?? []}
          loggedIn={props.loggedIn}
          hasBusiness={props.hasBusiness}
          routes={props.routes}
          csrfToken={props.csrfToken}
          page={props.page}
          userDisplay={userDisplay}
        />
      ) : LANDING_SECTION_PAGE_TO_SLUG[props.page] ? (
        <LandingSectionPage
          section={
            LANDING_SECTION_PAGE_TO_SLUG[props.page] as LandingSectionSlug
          }
          loggedIn={props.loggedIn}
          hasBusiness={props.hasBusiness}
          routes={props.routes}
          csrfToken={props.csrfToken}
          page={props.page}
          userDisplay={userDisplay}
          trades={props.trades}
        />
      ) : PRODUCT_PAGE_TO_SLUG[props.page] ? (
        <ProductLandingPage
          product={PRODUCT_PAGE_TO_SLUG[props.page] as ProductLandingSlug}
          loggedIn={props.loggedIn}
          hasBusiness={props.hasBusiness}
          hasOrganization={props.hasOrganization}
          canAccessBackend={props.canAccessBackend}
          routes={props.routes}
          csrfToken={props.csrfToken}
          page={props.page}
          userDisplay={userDisplay}
          appStore={props.appStore}
        />
      ) : props.page === "credential_share" && props.credentialShare ? (
        <CredentialShareViewPage credentialShare={props.credentialShare} />
      ) : props.page === "credential_share_missing" ? (
        <CredentialShareMissingPage />
      ) : (
        <div className="ds-shell">
          <AppHeader
            variant="app"
            loggedIn={props.loggedIn}
            hasBusiness={props.hasBusiness}
            hasOrganization={props.hasOrganization}
            canAccessBackend={props.canAccessBackend}
            hasJobSitePhotos={Boolean(props.hasJobSitePhotos)}
            hasCredentials={Boolean(props.hasCredentials)}
            routes={props.routes}
            csrfToken={props.csrfToken}
            page={props.page}
            unreadQuoteCount={unreadQuoteCount}
            incomingOrgInvitationCount={props.incomingOrgInvitationCount ?? 0}
            userDisplay={userDisplay}
            admin={props.admin}
            adminPath={props.adminPath}
            adminRoutes={props.adminRoutes}
          />
          {isDashboardPage(props.page) ? (
            <DashboardLayout
              routes={props.routes}
              adminRoutes={props.adminRoutes}
              admin={props.admin}
              canAccessBackend={Boolean(props.canAccessBackend)}
              unreadQuoteCount={unreadQuoteCount}
              incomingOrgInvitationCount={props.incomingOrgInvitationCount ?? 0}
              hasWebsite={props.hasBusiness}
              hasJobSitePhotos={Boolean(props.hasJobSitePhotos)}
              hasCredentials={Boolean(props.hasCredentials)}
              fillViewport={
                props.page === "dashboard" || props.page === "teammate_home"
              }
            >
              {props.page === "products_home" && (
                <ProductsHomePage
                  routes={props.routes}
                  hasJobSitePhotos={Boolean(props.hasJobSitePhotos)}
                  hasCredentials={Boolean(props.hasCredentials)}
                  billing={props.billing}
                />
              )}
              {props.page === "jobs" && (
                <JobsListPage
                  routes={props.routes}
                  jobs={props.jobs}
                  canCreateJobs={Boolean(props.canCreateJobs)}
                  showTeamColumns={Boolean(props.canAccessBackend)}
                />
              )}
              {props.page === "job_new" && (
                <JobNewPage
                  csrfToken={props.csrfToken}
                  googleMapsApiKey={props.googleMapsApiKey}
                  routes={props.routes}
                  jobForm={props.jobForm}
                />
              )}
              {props.page === "job_show" && props.job && (
                <JobShowPage
                  csrfToken={props.csrfToken}
                  googleMapsApiKey={props.googleMapsApiKey}
                  routes={props.routes}
                  job={props.job}
                  jobForm={props.jobForm}
                  jobPhotos={props.jobPhotos}
                  canAccessBackend={Boolean(props.canAccessBackend)}
                />
              )}
              {props.page === "credentials" && (
                <CredentialsListPage
                  routes={props.routes}
                  credentials={props.credentials}
                  showTeamColumns={Boolean(props.canAccessBackend)}
                />
              )}
              {props.page === "credential_new" && (
                <CredentialNewPage
                  csrfToken={props.csrfToken}
                  routes={props.routes}
                  credentialForm={props.credentialForm}
                  credentialHolders={props.credentialHolders}
                />
              )}
              {props.page === "credential_show" && props.credential && (
                <CredentialShowPage
                  csrfToken={props.csrfToken}
                  routes={props.routes}
                  credential={props.credential}
                  credentialForm={props.credentialForm}
                />
              )}
              {props.page === "credentials_roster" && (
                <CredentialsListPage
                  routes={props.routes}
                  credentials={props.credentials}
                  showTeamColumns
                />
              )}
              {props.page === "settings" && (
                <SettingsPage
                  csrfToken={props.csrfToken}
                  routes={props.routes}
                  organization={props.organization}
                  tradeOptions={props.tradeOptions}
                  billing={props.billing}
                  hasBusiness={props.hasBusiness}
                  canAccessBackend={Boolean(props.canAccessBackend)}
                />
              )}
              {props.page === "dashboard" && (
                <DashboardPage
                  csrfToken={props.csrfToken}
                  googleMapsApiKey={props.googleMapsApiKey}
                  routes={props.routes}
                  businessForm={props.businessForm}
                  siteUrl={props.siteUrl}
                  contactEmail={props.contactEmail}
                  initialMessage={props.initialMessage}
                  initialPrompt={props.initialPrompt}
                  agentError={props.agentError}
                  unreadQuoteCount={unreadQuoteCount}
                  billing={props.billing}
                  designConfig={props.designConfig}
                  userDisplay={userDisplay}
                  onUserDisplayChange={onUserDisplayChange}
                  hasWebsite={props.hasBusiness}
                />
              )}
              {props.page === "quotes" && (
                <QuotesPage
                  csrfToken={props.csrfToken}
                  routes={props.routes}
                  quotes={props.quotes}
                  unreadQuoteCount={unreadQuoteCount}
                  upcomingVisits={props.upcomingVisits}
                  onQuoteMarkedRead={onQuoteMarkedRead}
                />
              )}
              {props.page === "features" && (
                <FeaturesPage
                  featuresChecklist={props.featuresChecklist}
                  routes={props.routes}
                  featuresGbpSummary={
                    props.featuresGbpSummary ?? {
                      googlePlaceId: null,
                      siteUrl: "",
                      napName: "",
                      napPhone: null,
                      napServiceArea: null,
                      gbpReviewUrl: null,
                    }
                  }
                />
              )}
              {props.page === "account" && (
                <AccountPage
                  csrfToken={props.csrfToken}
                  routes={props.routes}
                  userDisplay={userDisplay}
                  canAccessBackend={Boolean(props.canAccessBackend)}
                  hasBusiness={props.hasBusiness}
                />
              )}
              {props.page === "billing" && (
                <BillingPage
                  csrfToken={props.csrfToken}
                  routes={props.routes}
                  billing={props.billing}
                  billingReturn={props.billingReturn}
                  organization={props.organization}
                />
              )}
              {props.page === "org_members" && (
                <OrgMembersPage
                  csrfToken={props.csrfToken}
                  routes={props.routes}
                  orgMembers={props.orgMembers}
                  orgInvitations={props.orgInvitations}
                  incomingOrgInvitations={props.incomingOrgInvitations}
                  organization={props.organization}
                  membershipRole={props.membershipRole}
                  currentUserId={props.currentUserId}
                  userDisplay={userDisplay}
                />
              )}
              {props.page === "teammate_home" && (
                <TeammateHomePage
                  csrfToken={props.csrfToken}
                  organization={props.organization}
                  membershipRole={props.membershipRole}
                  routes={props.routes}
                  hasCredentials={Boolean(props.hasCredentials)}
                  hasJobSitePhotos={Boolean(props.hasJobSitePhotos)}
                  incomingOrgInvitations={props.incomingOrgInvitations}
                  initialMessage={props.initialMessage}
                  userDisplay={userDisplay}
                  onUserDisplayChange={onUserDisplayChange}
                />
              )}
            </DashboardLayout>
          ) : (
            <>
              <main className="ds-container ds-main-below-app-header px-4 sm:pb-10">
                {props.page === "login" && (
                  <LoginPage
                    csrfToken={props.csrfToken}
                    routes={props.routes}
                    oauthProviders={props.oauthProviders}
                    userForm={props.userForm}
                  />
                )}
                {props.page === "signup" && (
                  <SignupPage
                    csrfToken={props.csrfToken}
                    routes={props.routes}
                    oauthProviders={props.oauthProviders}
                    userForm={props.userForm}
                  />
                )}
                {props.page === "verify_email" && (
                  <VerifyEmailPage
                    csrfToken={props.csrfToken}
                    routes={props.routes}
                    userForm={props.userForm}
                  />
                )}
                {props.page === "forgot_password" && (
                  <ForgotPasswordPage
                    csrfToken={props.csrfToken}
                    routes={props.routes}
                    userForm={props.userForm}
                  />
                )}
                {props.page === "reset_password" && (
                  <ResetPasswordPage
                    csrfToken={props.csrfToken}
                    routes={props.routes}
                    passwordResetToken={props.passwordResetToken}
                  />
                )}
                {props.page === "org_setup" && (
                  <OrgSetupPage
                    csrfToken={props.csrfToken}
                    routes={props.routes}
                    orgSetupForm={props.orgSetupForm}
                    incomingOrgInvitations={props.incomingOrgInvitations}
                    tradeOptions={props.tradeOptions}
                    userDisplay={userDisplay}
                  />
                )}
                {props.page === "website_setup" && (
                  <WebsiteSetupPage
                    csrfToken={props.csrfToken}
                    routes={props.routes}
                    websiteForm={props.websiteForm}
                  />
                )}
                {props.page === "terms" && <TermsPage routes={props.routes} />}
                {props.page === "privacy" && (
                  <PrivacyPage routes={props.routes} />
                )}
                {props.page === "faq" && (
                  <FaqPage routes={props.routes} faqs={props.faqs} />
                )}
              </main>
              {(props.page === "terms" ||
                props.page === "privacy" ||
                props.page === "faq") && <LegalFooter routes={props.routes} />}
            </>
          )}
        </div>
      )}
    </AppToastProvider>
  </CurrentPathProvider>
);

const MainAppPage: React.FC<MainAppPageProps> = (props) => {
  const [userDisplay, setUserDisplay] = useState(props.userDisplay ?? null);
  const [unreadQuoteCount, setUnreadQuoteCount] = useState(
    () => props.unreadQuoteCount ?? 0,
  );

  useEffect(() => {
    setUnreadQuoteCount(props.unreadQuoteCount ?? 0);
  }, [props.unreadQuoteCount]);

  const onUserDisplayChange = useCallback((updates: Partial<UserDisplay>) => {
    setUserDisplay((prev) => (prev ? { ...prev, ...updates } : null));
  }, []);

  const onQuoteMarkedRead = useCallback(() => {
    setUnreadQuoteCount((c) => Math.max(0, c - 1));
  }, []);

  return (
    <ThemeProvider>
      {mainPageContent(
        props,
        userDisplay,
        onUserDisplayChange,
        unreadQuoteCount,
        onQuoteMarkedRead,
      )}
    </ThemeProvider>
  );
};

export default MainAppPage;
